import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import {
  IconCheck,
  IconClipboardCheck,
  IconSearch,
  type Icon,
} from "@tabler/icons-react";
import { registryPresentation } from "../registryPresentation";

/**
 * What the desktop registry slide shows while the registry is searched (Figma
 * "D7.identite.verification"): the three things the lookup does, in order.
 *
 * Drawn at rest, all three at the same weight. The lookup is ONE request, so
 * there is no moment at which "searching" is done and "matching" has begun:
 * lighting the rows one after the other would be a timer pretending to be
 * progress. The slide leaves this state when the answer lands.
 */
export function RegistryVerifying({
  registry,
}: {
  registry: keyof typeof registryPresentation;
}): React.JSX.Element {
  const { t } = useTranslation();

  const rows: readonly { icon: Icon; label: string }[] = [
    { icon: IconClipboardCheck, label: t("Details submitted") },
    {
      icon: IconSearch,
      label:
        registry === "rpps"
          ? t("Searching the RPPS")
          : t("Searching {{registry}}", {
              registry: registryPresentation[registry].sourceName,
            }),
    },
    { icon: IconCheck, label: t("Matching your profile") },
  ];

  return (
    <ol className="m-0 flex list-none flex-col p-0">
      {rows.map(({ icon: RowIcon, label }, index) => (
        <Fragment key={label}>
          {index > 0 && (
            <li aria-hidden className="flex h-3 w-8 justify-center">
              <span className="h-full w-px bg-ds-black/48" />
            </li>
          )}
          <li className="flex items-center gap-1.25 pl-2 opacity-48">
            <RowIcon className="size-4 shrink-0 text-ds-black" stroke={1.25} />
            <span className="font-dm-sans text-ui-note text-ds-black">
              {label}
            </span>
          </li>
        </Fragment>
      ))}
    </ol>
  );
}
